import { Box } from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { IS_SA } from '../../../../../config/appConfig';
import { AppRoutes } from '../../../../../constants/appRoutes';
import { GTMEvents } from '../../../../../constants/gtmEvents';
import { InvestorChannels } from '../../../../../constants/investorChannels';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { buildLocalizeRoute } from '../../../../../helpers/languageHelper';
import { identifyOpenPanel, trackOpenPanelView } from '../../../../../helpers/openPanelHelper';
import { pushToDataLayer, saveContactNumber } from '../../../../../helpers/pushToGTMHelper';
import { OpenPanelCategories, OpenPanelEvents } from '../../../../../constants/openPanelEvents';
import { getUser, Logout, loginUsingToken } from '../../../../../services/authService';
import { UserService } from '../../../../../services/userService';
import { Toaster } from '../../../../../shared/components/common/toaster';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { BaseRegistration } from '../../../core/baseRegistration';
import { SignupPageWrapper } from '../../../core/signUpPageWrapper';

export const InvestorBaseRegistration = ({ isKsaInternationalInvestor = false }) => {
	const userService = new UserService();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const [loading, setLoading] = useState(false);
	const [showOTPAuth, setShowOTPAuth] = useState(false);
    const enteredInternationalFlow = !!isKsaInternationalInvestor || !!location?.state?.isKsaInternationalInvestor;
	const [persistedInternationalFlow, setPersistedInternationalFlow] = useState(
		localStorage.getItem("isKsaInternationalInvestor") === '1'
	);
    const isKsaInternationalFlow = IS_SA && (enteredInternationalFlow || persistedInternationalFlow);

	useEffect(() => {
		trackOpenPanelView(OpenPanelEvents.signupStarted, {
			page_name: 'Sign up',
			category: OpenPanelCategories.onboarding,
		});
	}, []);

    useEffect(() => {
        if (!IS_SA) return;
        if (enteredInternationalFlow) {
			if (!persistedInternationalFlow) {
				localStorage.setItem("isKsaInternationalInvestor", '1');
				setPersistedInternationalFlow(true);
			}
            return;
        }

		if (persistedInternationalFlow) {
			localStorage.removeItem("isKsaInternationalInvestor");
			setPersistedInternationalFlow(false);
		}
    }, [enteredInternationalFlow, persistedInternationalFlow]);
	const onSubmit = (values) => {
		const data = {
			...values,
			referral: localStorage.getItem('ref'),
			channel: InvestorChannels.Core,
			isKsaInternationalInvestor: isKsaInternationalFlow
		};
		if (!data.termsConditionsForInvestmentsAccepted) {
			Toaster({ msg: t('termsAndConditionsRequired') });
			return;
		}

		setLoading(true);
		userService
			.registerInvestor(data)
			.then((resp) => {
				let tokenModel = resp.data;
				if (tokenModel) {
					setLoading(false);
					localStorage.removeItem('ref');
					const email = data.email;
					const phone = `+${data?.contactNumber?.code} ${data?.contactNumber?.number}`;
					saveContactNumber(phone);
					pushToDataLayer(GTMEvents.signUpSubmit, {
						userInfo: {
							email: email,
							phone: phone
						}
					});
					if (!resp.data?.emailConfirmed) {
						navigate(buildLocalizeRoute(`${AppRoutes.VerifyEmail}?e=${encodeURIComponent(data.email)}&type=2`));
					} else {
						localStorage.removeItem("isKsaInternationalInvestor");
						setPersistedInternationalFlow(false);
						loginUsingToken(tokenModel.token);
						identifyOpenPanel(getUser(), {
							account_status: 'pending',
							...(data?.contactNumber?.code && data?.contactNumber?.number
								? { mobile_number: `+${data.contactNumber.code}${data.contactNumber.number}` }
								: {}),
						});
						if (tokenModel.otpRequested) {
							setShowOTPAuth(true);
						} else {
							onNavigateNextStep();
						}
					}
				}
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const onNavigateNextStep = () => {
		const nextStep = IS_SA && !isKsaInternationalFlow ? AppRoutes.IRegNafathVerification : AppRoutes.IRegPersonalInfo;
		const route = `${AppRoutes.InvestorRegistration}/${nextStep}`;
		navigate(buildLocalizeRoute(route), { state: { isKsaInternationalInvestor: isKsaInternationalFlow } });
	};

	return (
		<Box className="flex flex-col h-full">
			<SignupPageWrapper>
				<BaseRegistration
					loading={loading}
					clientType={'investor'}
					isSA={IS_SA && !isKsaInternationalFlow}
					onSubmitForm={(values) => {
						onSubmit(values);
					}}></BaseRegistration>
			</SignupPageWrapper>
			{showOTPAuth ? (
				<OTPAuthenticationDialog
					type={OTPTypes.Authentication}
					onClose={(isVerified) => {
						setShowOTPAuth(false);
						if (isVerified) {
							onNavigateNextStep();
						} else {
							Logout();
							navigate(buildLocalizeRoute(AppRoutes.Login));
						}
					}}
					state={showOTPAuth}></OTPAuthenticationDialog>
			) : null}
		</Box>
	);
};
